import { useState, useEffect, useRef } from "react";
import { addBatchTasks, type SafetyTask } from "@/mocks/sharedState";

interface RiskUser {
  id: string;
  name: string;
  address: string;
  district: string;
  riskScore: number;
  riskLevel: "极高" | "高" | "中" | "低";
  factors: string[];
  lastInspection: string;
  nextDue: string;
  aiSuggestion: string;
  phone: string;
  selected?: boolean;
}

interface Inspector {
  id: string;
  name: string;
  district: string[];
  maxDaily: number;
  assigned: number;
}

const riskUsers: RiskUser[] = [
  { id: "R001", name: "李秀梅", address: "海淀区中关村南大街15号", district: "海淀区", riskScore: 92, riskLevel: "极高", factors: ["设备老化>10年", "上次安检发现泄漏", "独居老人"], lastInspection: "2025-06-15", nextDue: "2026-04-15", aiSuggestion: "建议立即安排上门安检，优先级P0", phone: "138****1234" },
  { id: "R002", name: "张建国", address: "朝阳区望京街道1号楼", district: "朝阳区", riskScore: 78, riskLevel: "高", factors: ["软管老化", "通风不良", "逾期未检"], lastInspection: "2025-09-20", nextDue: "2026-04-20", aiSuggestion: "本周内安排安检，重点检查软管和通风", phone: "139****5678" },
  { id: "R003", name: "王大明", address: "丰台区南四环西路128号", district: "丰台区", riskScore: 65, riskLevel: "中", factors: ["设备使用>5年", "近期用气量异常"], lastInspection: "2025-11-10", nextDue: "2026-05-10", aiSuggestion: "下月安排常规安检，关注用气量变化", phone: "137****9012" },
  { id: "R004", name: "陈小红", address: "西城区西单北大街56号", district: "西城区", riskScore: 45, riskLevel: "中", factors: ["安检即将到期"], lastInspection: "2025-12-01", nextDue: "2026-06-01", aiSuggestion: "按计划安排安检，无特殊风险", phone: "136****3456" },
  { id: "R005", name: "刘建华", address: "石景山区古城路15号", district: "石景山区", riskScore: 28, riskLevel: "低", factors: ["新装用户"], lastInspection: "2026-01-15", nextDue: "2027-01-15", aiSuggestion: "正常周期安检即可", phone: "135****7890" },
  { id: "R006", name: "赵美丽", address: "通州区运河东大街88号", district: "通州区", riskScore: 82, riskLevel: "高", factors: ["燃气报警器失效", "厨房改造"], lastInspection: "2025-08-20", nextDue: "2026-04-25", aiSuggestion: "优先安排，检查报警器和改造合规性", phone: "134****2345" },
  { id: "R007", name: "孙志远", address: "朝阳区三里屯路22号", district: "朝阳区", riskScore: 88, riskLevel: "极高", factors: ["设备老化>15年", "多次投诉"], lastInspection: "2025-05-10", nextDue: "2026-04-12", aiSuggestion: "紧急安排，设备严重老化需更换", phone: "133****6789" },
  { id: "R008", name: "周丽华", address: "海淀区上地信息路8号", district: "海淀区", riskScore: 71, riskLevel: "高", factors: ["软管超期", "独居老人"], lastInspection: "2025-10-05", nextDue: "2026-04-22", aiSuggestion: "本周安排，重点检查软管更换", phone: "132****0123" },
];

const inspectors: Inspector[] = [
  { id: "I001", name: "王建军", district: ["朝阳区", "东城区"], maxDaily: 12, assigned: 0 },
  { id: "I002", name: "李明", district: ["海淀区", "西城区"], maxDaily: 10, assigned: 0 },
  { id: "I003", name: "张伟", district: ["丰台区", "石景山区"], maxDaily: 11, assigned: 0 },
  { id: "I004", name: "赵强", district: ["通州区", "顺义区"], maxDaily: 9, assigned: 0 },
];

const riskColors: Record<string, { bg: string; text: string; bar: string; border: string }> = {
  "极高": { bg: "bg-red-50", text: "text-red-600", bar: "bg-red-500", border: "border-red-200" },
  "高": { bg: "bg-orange-50", text: "text-orange-600", bar: "bg-orange-500", border: "border-orange-200" },
  "中": { bg: "bg-amber-50", text: "text-amber-600", bar: "bg-amber-400", border: "border-amber-200" },
  "低": { bg: "bg-green-50", text: "text-green-600", bar: "bg-green-500", border: "border-green-200" },
};

// AI Scheduling Algorithm: sort by risk score + due date proximity + geographic clustering
function runSchedulingAlgorithm(users: RiskUser[], insp: Inspector[]) {
  const today = new Date("2026-04-10");
  const scored = users.map(u => {
    const daysUntilDue = Math.max(0, (new Date(u.nextDue).getTime() - today.getTime()) / 86400000);
    const urgencyScore = u.riskScore * 0.6 + Math.max(0, 30 - daysUntilDue) * 1.5;
    return { ...u, urgencyScore };
  }).sort((a, b) => b.urgencyScore - a.urgencyScore);

  const result: { date: string; inspector: Inspector; users: RiskUser[]; district: string }[] = [];
  const inspCopy = insp.map(i => ({ ...i, assigned: 0 }));
  const dates = ["2026-04-11", "2026-04-12", "2026-04-13", "2026-04-14", "2026-04-15"];

  let userIdx = 0;
  for (const date of dates) {
    for (const insp of inspCopy) {
      const dayUsers: RiskUser[] = [];
      const remaining = scored.slice(userIdx);
      // Prefer users in inspector's district
      const preferred = remaining.filter(u => insp.district.includes(u.district));
      const others = remaining.filter(u => !insp.district.includes(u.district));
      const pool = [...preferred, ...others];
      const limit = Math.min(insp.maxDaily, pool.length, 5);
      for (let i = 0; i < limit; i++) {
        const u = pool[i];
        if (u && !dayUsers.find(d => d.id === u.id)) {
          dayUsers.push(u);
        }
      }
      if (dayUsers.length > 0) {
        result.push({ date, inspector: insp, users: dayUsers, district: insp.district[0] });
        userIdx += dayUsers.length;
      }
    }
    if (userIdx >= scored.length) break;
  }
  return result;
}

export default function SafetyAIRisk() {
  const [activeTab, setActiveTab] = useState<"risk" | "schedule" | "batch" | "model">("risk");
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState("");
  const [sortBy, setSortBy] = useState<"score" | "date">("score");
  const [filterLevel, setFilterLevel] = useState<string>("全部");
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [batchInspector, setBatchInspector] = useState("I001");
  const [batchDate, setBatchDate] = useState("2026-04-15");
  const [batchAssigned, setBatchAssigned] = useState(false);
  const [scheduleResult, setScheduleResult] = useState<ReturnType<typeof runSchedulingAlgorithm>>([]);
  const [schedulingRun, setSchedulingRun] = useState(false);
  const radarRef = useRef<HTMLCanvasElement>(null);

  const filtered = riskUsers.filter(u => filterLevel === "全部" || u.riskLevel === filterLevel);
  const sorted = [...filtered].sort((a, b) =>
    sortBy === "score" ? b.riskScore - a.riskScore : a.nextDue.localeCompare(b.nextDue)
  );

  const toggleSelect = (id: string) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedUsers.size === sorted.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(sorted.map(u => u.id)));
    }
  };

  const handleBatchAssign = () => {
    if (selectedUsers.size === 0) return;
    const inspector = inspectors.find(i => i.id === batchInspector);
    if (!inspector) return;

    // Write tasks to shared state for inspector app sync
    const newTasks: SafetyTask[] = Array.from(selectedUsers).map((userId, idx) => {
      const user = riskUsers.find(u => u.id === userId);
      if (!user) return null;
      return {
        id: `SC-BATCH-${Date.now()}-${idx}`,
        userName: user.name,
        address: user.address,
        phone: user.phone,
        type: user.riskLevel === "极高" || user.riskLevel === "高" ? "紧急安检" : "年度安检",
        scheduledTime: "09:00-18:00",
        scheduledDate: batchDate,
        status: "待安检" as const,
        priority: (user.riskLevel === "极高" ? "urgent" : "normal") as "urgent" | "normal",
        inspectorId: batchInspector,
        inspectorName: inspector.name,
        riskLevel: user.riskLevel,
        district: user.district,
        assignedAt: new Date().toLocaleString("zh-CN", { hour12: false }).replace(/\//g, "-"),
      };
    }).filter(Boolean) as SafetyTask[];

    addBatchTasks(newTasks);
    setBatchAssigned(true);
    setTimeout(() => setBatchAssigned(false), 3000);
    setSelectedUsers(new Set());
  };

  const runSchedule = () => {
    setSchedulingRun(true);
    setTimeout(() => {
      setScheduleResult(runSchedulingAlgorithm(riskUsers, inspectors));
    }, 1200);
  };

  useEffect(() => {
    if (activeTab !== "model") return;
    const canvas = radarRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width; const H = canvas.height;
    const cx = W / 2; const cy = H / 2;
    const R = Math.min(W, H) / 2 - 40;
    ctx.clearRect(0, 0, W, H);
    const labels = ["设备老化", "泄漏历史", "用气异常", "安检逾期", "环境风险", "用户特征"];
    const values = [0.85, 0.72, 0.68, 0.91, 0.55, 0.78];
    const n = labels.length;
    for (let r = 1; r <= 5; r++) {
      ctx.beginPath(); ctx.strokeStyle = "rgba(0,0,0,0.08)"; ctx.lineWidth = 1;
      for (let i = 0; i < n; i++) {
        const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
        const x = cx + (R * r / 5) * Math.cos(angle); const y = cy + (R * r / 5) * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath(); ctx.stroke();
    }
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      ctx.beginPath(); ctx.strokeStyle = "rgba(0,0,0,0.1)";
      ctx.moveTo(cx, cy); ctx.lineTo(cx + R * Math.cos(angle), cy + R * Math.sin(angle)); ctx.stroke();
    }
    ctx.beginPath();
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const x = cx + R * values[i] * Math.cos(angle); const y = cy + R * values[i] * Math.sin(angle);
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath(); ctx.fillStyle = "rgba(232,83,26,0.15)"; ctx.fill();
    ctx.strokeStyle = "#E8531A"; ctx.lineWidth = 2; ctx.stroke();
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const x = cx + R * values[i] * Math.cos(angle); const y = cy + R * values[i] * Math.sin(angle);
      ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fillStyle = "#E8531A"; ctx.fill();
    }
    for (let i = 0; i < n; i++) {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      const x = cx + (R + 28) * Math.cos(angle); const y = cy + (R + 28) * Math.sin(angle);
      ctx.fillStyle = "#374151"; ctx.font = "12px sans-serif"; ctx.textAlign = "center";
      ctx.fillText(labels[i], x, y + 4);
      ctx.fillStyle = "#E8531A"; ctx.font = "bold 10px sans-serif";
      ctx.fillText(`${Math.round(values[i] * 100)}%`, x, y + 16);
    }
  }, [activeTab]);

  const generateReport = () => {
    setGenerating(true); setReport("");
    const text = `【AI安检风险评估报告】生成时间：2026-04-10 14:30\n\n一、风险概况\n当前系统共监测用户 1,256 户，AI风险评估模型识别出：\n• 极高风险用户：3户（占比0.24%），需立即处置\n• 高风险用户：12户（占比0.96%），本周内安排\n• 中风险用户：89户（占比7.09%），按计划安排\n• 低风险用户：1,152户（占比91.71%），正常周期\n\n二、主要风险因素分析\n1. 设备老化（权重35%）：共发现设备使用年限超10年用户47户，其中18户超15年，存在较高泄漏风险。\n2. 安检逾期（权重28%）：当前逾期未检用户23户，最长逾期达8个月，建议优先处理。\n3. 历史泄漏记录（权重20%）：有泄漏历史的用户复发概率是普通用户的3.2倍，需重点关注。\n4. 用气异常（权重17%）：AI检测到15户用气量异常波动，可能存在设备故障或违规用气。\n\n三、智能排期建议\n基于AI算法综合考虑风险等级、地理位置、安检员工作量，建议本周安排：\n• 4月11日：8户（朝阳、海淀区高风险用户）\n• 4月12日：12户（东城、西城区集中安检）\n• 4月13日：10户（丰台、石景山区）\n• 4月14日：6户（通州区）\n• 4月15日：15户（全区域高优先级用户）\n\n四、AI模型置信度\n本次评估模型准确率：94.3%，基于历史12,000+安检案例训练，持续优化中。`;
    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) { setReport(prev => prev + text[i]); i++; }
      else { clearInterval(interval); setGenerating(false); }
    }, 12);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-red-50 to-orange-50 px-5 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
              <i className="ri-brain-line text-red-600 text-lg"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">AI风险评估模型 v2.0</h3>
              <p className="text-xs text-gray-500">多维度智能安检风险预测 · 批量任务分配 · 智能排期优化</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">准确率 94.3%</div>
            <div className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-medium">极高风险 {riskUsers.filter(u => u.riskLevel === "极高").length}户</div>
          </div>
        </div>
        <div className="flex gap-1 mt-3 bg-white/60 rounded-lg p-1 w-fit">
          {[
            { key: "risk", label: "风险用户" },
            { key: "batch", label: "批量分配" },
            { key: "schedule", label: "智能排期" },
            { key: "model", label: "模型分析" },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key as typeof activeTab)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-5">
        {/* ===== RISK USERS ===== */}
        {activeTab === "risk" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex gap-2 flex-wrap">
                {["全部", "极高", "高", "中", "低"].map(level => (
                  <button key={level} onClick={() => setFilterLevel(level)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterLevel === level ? "bg-[#E8531A] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                    {level === "全部" ? "全部" : `${level}风险`} {level !== "全部" ? riskUsers.filter(u => u.riskLevel === level).length : riskUsers.length}
                  </button>
                ))}
              </div>
              <select value={sortBy} onChange={e => setSortBy(e.target.value as "score" | "date")}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#E8531A] cursor-pointer">
                <option value="score">按风险评分</option>
                <option value="date">按到期日期</option>
              </select>
            </div>
            <div className="space-y-2">
              {sorted.map(user => {
                const cfg = riskColors[user.riskLevel];
                return (
                  <div key={user.id} className={`${cfg.bg} border ${cfg.border} rounded-xl p-4`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>{user.riskLevel}风险</span>
                        <span className="text-sm font-semibold text-gray-800">{user.name}</span>
                        <span className="text-xs text-gray-400">{user.id}</span>
                      </div>
                      <div className="text-right">
                        <div className={`text-xl font-bold ${cfg.text}`}>{user.riskScore}</div>
                        <div className="text-[10px] text-gray-400">风险评分</div>
                      </div>
                    </div>
                    <div className="w-full bg-white/60 rounded-full h-1.5 mb-2">
                      <div className={`h-1.5 rounded-full ${cfg.bar}`} style={{ width: `${user.riskScore}%` }} />
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-xs text-gray-500 mb-1">{user.address}</div>
                        <div className="flex flex-wrap gap-1">
                          {user.factors.map((f, i) => (
                            <span key={i} className="text-[10px] bg-white/70 text-gray-600 px-1.5 py-0.5 rounded">{f}</span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[10px] text-gray-400">到期日</div>
                        <div className={`text-xs font-medium ${cfg.text}`}>{user.nextDue}</div>
                      </div>
                    </div>
                    <div className="mt-2 bg-white/60 rounded-lg px-3 py-2 flex items-start gap-2">
                      <i className="ri-robot-2-line text-[#E8531A] text-xs mt-0.5 flex-shrink-0"></i>
                      <span className="text-xs text-gray-600">{user.aiSuggestion}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== BATCH ASSIGNMENT ===== */}
        {activeTab === "batch" && (
          <div className="space-y-4">
            {batchAssigned && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                <i className="ri-check-double-line text-green-600"></i>
                <span className="text-sm text-green-700 font-medium">批量分配成功！任务已下发至安检员</span>
              </div>
            )}

            {/* Batch config */}
            <div className="bg-orange-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-800 mb-3">批量分配配置</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">指派安检员</label>
                  <select value={batchInspector} onChange={e => setBatchInspector(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A] bg-white">
                    {inspectors.map(i => (
                      <option key={i.id} value={i.id}>{i.name} ({i.district.join("/")})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">安检日期</label>
                  <input type="date" value={batchDate} onChange={e => setBatchDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A] bg-white" />
                </div>
              </div>
            </div>

            {/* User selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-gray-800">选择安检用户</div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500">已选 {selectedUsers.size} 户</span>
                  <button onClick={selectAll} className="text-xs text-[#E8531A] cursor-pointer whitespace-nowrap">
                    {selectedUsers.size === sorted.length ? "取消全选" : "全选"}
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {riskUsers.map(user => {
                  const cfg = riskColors[user.riskLevel];
                  const isSelected = selectedUsers.has(user.id);
                  return (
                    <div key={user.id}
                      onClick={() => toggleSelect(user.id)}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? "border-[#E8531A] bg-orange-50/50" : "border-gray-100 bg-white hover:border-gray-200"}`}>
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-[#E8531A]" : "border-2 border-gray-300"}`}>
                        {isSelected && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-800">{user.name}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cfg.bg} ${cfg.text}`}>{user.riskLevel}</span>
                        </div>
                        <div className="text-xs text-gray-400 truncate">{user.address}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className={`text-sm font-bold ${cfg.text}`}>{user.riskScore}</div>
                        <div className="text-[10px] text-gray-400">{user.nextDue}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Batch actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  const highRisk = riskUsers.filter(u => u.riskLevel === "极高" || u.riskLevel === "高");
                  setSelectedUsers(new Set(highRisk.map(u => u.id)));
                }}
                className="border border-[#E8531A] text-[#E8531A] py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-orange-50 transition-colors whitespace-nowrap">
                <i className="ri-filter-line mr-1"></i>选高风险用户
              </button>
              <button
                onClick={() => {
                  const overdue = riskUsers.filter(u => u.nextDue <= "2026-04-20");
                  setSelectedUsers(new Set(overdue.map(u => u.id)));
                }}
                className="border border-amber-500 text-amber-600 py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-amber-50 transition-colors whitespace-nowrap">
                <i className="ri-time-line mr-1"></i>选即将到期
              </button>
            </div>

            <button
              onClick={handleBatchAssign}
              disabled={selectedUsers.size === 0}
              className="w-full bg-[#E8531A] text-white py-3 rounded-xl text-sm font-semibold cursor-pointer hover:bg-[#d44a15] disabled:opacity-50 transition-colors whitespace-nowrap">
              <i className="ri-send-plane-line mr-2"></i>
              批量分配 {selectedUsers.size > 0 ? `${selectedUsers.size}户` : ""} 安检任务
            </button>

            {/* Inspector workload */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-sm font-semibold text-gray-800 mb-3">安检员工作量</div>
              <div className="space-y-2">
                {inspectors.map(insp => (
                  <div key={insp.id} className="flex items-center gap-3">
                    <div className="w-16 text-xs text-gray-600 flex-shrink-0">{insp.name}</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div className="bg-[#E8531A] h-2 rounded-full transition-all" style={{ width: `${(insp.assigned / insp.maxDaily) * 100}%` }} />
                    </div>
                    <div className="text-xs text-gray-500 flex-shrink-0">{insp.assigned}/{insp.maxDaily}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ===== SMART SCHEDULE ===== */}
        {activeTab === "schedule" && (
          <div className="space-y-4">
            <div className="bg-orange-50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-cpu-line text-[#E8531A]"></i>
                <span className="text-sm font-semibold text-gray-800">AI智能排期算法 v2.0</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">综合考量：风险评分(60%) + 到期紧迫度(25%) + 地理聚类(15%)，自动均衡安检员工作量</p>
              {!schedulingRun ? (
                <button onClick={runSchedule} className="w-full bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-semibold cursor-pointer hover:bg-[#d44a15] transition-colors whitespace-nowrap">
                  <i className="ri-robot-2-line mr-2"></i>运行AI排期算法
                </button>
              ) : scheduleResult.length === 0 ? (
                <div className="flex items-center justify-center gap-2 py-3">
                  <i className="ri-loader-4-line animate-spin text-[#E8531A]"></i>
                  <span className="text-sm text-gray-600">AI算法运行中...</span>
                </div>
              ) : null}
            </div>

            {scheduleResult.length > 0 && (
              <>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: "总排期", value: `${scheduleResult.reduce((s, r) => s + r.users.length, 0)}户`, color: "text-[#E8531A]" },
                    { label: "安检员", value: `${inspectors.length}人`, color: "text-amber-600" },
                    { label: "覆盖天数", value: `${new Set(scheduleResult.map(r => r.date)).size}天`, color: "text-green-600" },
                    { label: "预计完成率", value: "97%", color: "text-teal-600" },
                  ].map((s, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                      <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  {Array.from(new Set(scheduleResult.map(r => r.date))).map(date => {
                    const dayItems = scheduleResult.filter(r => r.date === date);
                    return (
                      <div key={date} className="bg-gray-50 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-[#E8531A] flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{date.slice(-2)}</span>
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-gray-800">{date}</div>
                              <div className="text-xs text-gray-400">共 {dayItems.reduce((s, r) => s + r.users.length, 0)} 户</div>
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {dayItems.map((item, i) => (
                            <div key={i} className="bg-white rounded-lg p-3 flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium text-gray-700">{item.inspector.name}</span>
                                  <span className="text-[10px] bg-orange-50 text-[#E8531A] px-1.5 py-0.5 rounded">{item.district}</span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {item.users.map(u => (
                                    <span key={u.id} className={`text-[10px] px-1.5 py-0.5 rounded ${riskColors[u.riskLevel].bg} ${riskColors[u.riskLevel].text}`}>
                                      {u.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="text-xs text-gray-400 flex-shrink-0">{item.users.length}户</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button className="w-full bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-[#d44a15] transition-colors whitespace-nowrap">
                  <i className="ri-send-plane-line mr-2"></i>一键下发全部排期任务
                </button>
              </>
            )}
          </div>
        )}

        {/* ===== MODEL ANALYSIS ===== */}
        {activeTab === "model" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-600 mb-2 text-center">风险因素权重雷达图</h4>
                <canvas ref={radarRef} width={280} height={240} className="w-full" />
              </div>
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-600 mb-2">模型特征权重</h4>
                {[
                  { label: "设备老化", weight: 35, color: "bg-red-500" },
                  { label: "安检逾期", weight: 28, color: "bg-orange-500" },
                  { label: "历史泄漏", weight: 20, color: "bg-amber-500" },
                  { label: "用气异常", weight: 17, color: "bg-yellow-500" },
                ].map((f, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-600">{f.label}</span>
                      <span className="font-medium text-gray-700">{f.weight}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className={`h-2 rounded-full ${f.color}`} style={{ width: `${f.weight * 2.5}%` }} />
                    </div>
                  </div>
                ))}
                <div className="bg-gray-50 rounded-xl p-3 mt-3">
                  <div className="text-xs text-gray-500 mb-1">模型训练数据</div>
                  <div className="text-sm font-bold text-gray-800">12,847 条</div>
                  <div className="text-xs text-gray-400">历史安检案例</div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-800">AI风险评估报告</h4>
                <button onClick={generateReport} disabled={generating}
                  className="flex items-center gap-1.5 bg-[#E8531A] text-white px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap">
                  <i className={`${generating ? "ri-loader-4-line animate-spin" : "ri-robot-2-line"}`}></i>
                  {generating ? "生成中..." : "生成报告"}
                </button>
              </div>
              {report && (
                <div className="bg-gray-50 rounded-xl p-4 max-h-64 overflow-y-auto">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{report}</pre>
                  {generating && <span className="inline-block w-1 h-3 bg-[#E8531A] animate-pulse ml-0.5"></span>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
